import { useState, useRef, useEffect } from "react";
import { API } from "@/lib/api";
import { startLogin } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowUp, Paperclip, Lock, Sparkles, Bot, User as UserIcon, Loader2, RotateCcw,
  Calculator, FileText, TrendingDown, ShieldAlert, Scale
} from "lucide-react";

const PILLS = [
  { label: "Estimate cost", icon: Calculator, q: "How much should it cost to build a 2000 sqft house in India?" },
  { label: "Review my BOQ", icon: FileText, q: "How do I check if my contractor's BOQ is over-quoted?" },
  { label: "Cut my costs", icon: TrendingDown, q: "What are the best ways to reduce my construction cost without compromising quality?" },
  { label: "Check risks", icon: ShieldAlert, q: "What construction risks should I watch out for before starting my house?" },
  { label: "Compare quotes", icon: Scale, q: "How do I compare two contractor quotations objectively?" },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function getSessionId() {
  let s = localStorage.getItem("nirmantra_public_chat");
  if (!s) { s = "pub_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("nirmantra_public_chat", s); }
  return s;
}

export default function LandingChat() {
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef();

  useEffect(() => {
    const s = getSessionId();
    setSessionId(s);
    fetch(`${API}/public/chat/${s}`).then((r) => r.json()).then((d) => { if (Array.isArray(d)) setMessages(d); }).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const FREE_LIMIT = 3;
  const [gate, setGate] = useState(false);

  const handleAttach = () => {
    toast.message("Sign in to attach documents", { description: "Continue your chat in your project dashboard, where uploads are analysed and saved." });
    localStorage.setItem("bg_chat_continue", "1");
    startLogin();
  };
  const continueSignIn = () => { localStorage.setItem("bg_chat_continue", "1"); startLogin(); };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming || !sessionId) return;
    const asked = messages.filter((m) => m.role === "user").length;
    if (asked >= FREE_LIMIT) { setGate(true); return; }
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/public/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: q }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const data = JSON.parse(part.slice(5).trim());
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't respond just now. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const newChat = () => {
    const s = "pub_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("nirmantra_public_chat", s);
    setSessionId(s); setMessages([]); setInput("");
  };

  const active = messages.length > 0;

  return (
    <section className="relative overflow-hidden" data-testid="landing-chat-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-72 w-[42rem] max-w-full rounded-full bg-[#0E9AC8]/10 blur-[120px]" />
      </div>
      <div className="max-w-3xl mx-auto px-6 pt-20 pb-16 lg:pt-24 lg:pb-20">
        {!active ? (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 mb-8 shadow-sm">
              <Sparkles className="h-3.5 w-3.5 text-[#0E9AC8]" strokeWidth={2} />
              <span className="text-xs font-semibold tracking-wide text-slate-600">Nirmantra AI · Powered by NCIS™</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold text-slate-900 flex items-center justify-center gap-3">
              <Sparkles className="h-7 w-7 sm:h-8 sm:w-8 text-[#F5923C]" strokeWidth={1.75} />
              <span>{greeting()}, let's build smarter.</span>
            </h2>
            <p className="mt-3 text-base text-slate-500">Ask anything about your construction budget, quotations, materials or risks — no sign-up needed.</p>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="relative h-9 w-9 rounded-xl bg-[#0E9AC8] flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" strokeWidth={1.75} />
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 border-2 border-white" />
              </div>
              <div>
                <p className="font-display font-bold text-slate-900 leading-tight">Nirmantra AI Advisor</p>
                <p className="text-[11px] text-[#0E9AC8] flex items-center gap-1"><Sparkles className="h-3 w-3" /> Powered by NCIS™</p>
              </div>
            </div>
            <button data-testid="landing-chat-new" onClick={newChat} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#0E9AC8] transition-colors">
              <RotateCcw className="h-3.5 w-3.5" /> New chat
            </button>
          </div>
        )}

        {/* Conversation */}
        {active && (
          <div ref={scrollRef} className="card-premium rounded-3xl p-5 space-y-4 max-h-[440px] overflow-y-auto mb-4" data-testid="landing-chat-messages">
            {messages.map((m, i) => (
              <div key={i} data-testid={`landing-chat-msg-${m.role}`} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${m.role === "user" ? "bg-slate-900" : "bg-[#0E9AC8]"}`}>
                  {m.role === "user" ? <UserIcon className="h-4 w-4 text-white" strokeWidth={1.75} /> : <Bot className="h-4 w-4 text-white" strokeWidth={1.75} />}
                </div>
                <div className={`max-w-[82%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap font-sans ${m.role === "user" ? "bg-slate-900 text-white rounded-tr-sm" : "bg-slate-50 border border-slate-100 text-slate-700 rounded-tl-sm"}`}>
                  {m.content || <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Composer */}
        <div>
          <div className="rounded-3xl border border-slate-200 bg-white shadow-[0_10px_40px_-24px_rgba(11,27,43,0.35)] p-4 transition-shadow focus-within:shadow-[0_16px_50px_-24px_rgba(14,154,200,0.5)] focus-within:border-[#0E9AC8]/50">
            <textarea
              data-testid="landing-chat-input"
              rows={active ? 1 : 2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask Nirmantra AI about your construction project…"
              className="w-full resize-none bg-transparent px-2 pt-1 text-base text-slate-800 placeholder:text-slate-400 outline-none"
            />
            <div className="flex items-center justify-between mt-2">
              <button type="button" data-testid="landing-chat-attach" onClick={handleAttach}
                className="h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[#0E9AC8] hover:text-[#0E9AC8] transition-colors" title="Attach a document">
                <Paperclip className="h-4 w-4" strokeWidth={2} />
              </button>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-600 rounded-lg bg-slate-100 px-2.5 py-1.5">NCIS™ Advisor</span>
                <Button data-testid="landing-chat-send" onClick={() => send()} disabled={streaming || gate}
                  className="h-9 w-9 p-0 rounded-full bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white transition-colors">
                  {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" strokeWidth={2.25} />}
                </Button>
              </div>
            </div>
          </div>

          {gate && (
            <div data-testid="landing-chat-gate" className="mt-4 animate-fade-up rounded-2xl border border-[#0E9AC8]/25 bg-[#0E9AC8]/5 px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-left">
                <div className="h-10 w-10 rounded-xl bg-[#0E9AC8]/15 flex items-center justify-center shrink-0"><Lock className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.75} /></div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">You've reached the free limit (3 questions)</p>
                  <p className="text-xs text-slate-500">Sign in to keep chatting — your conversation continues in your project dashboard with document analysis.</p>
                </div>
              </div>
              <Button data-testid="landing-chat-login" onClick={continueSignIn} className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-10 px-5 text-sm shrink-0 transition-colors">
                Sign in to continue
              </Button>
            </div>
          )}

          {!active && !gate && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {PILLS.map((p) => (
                <button key={p.label} data-testid="landing-chat-pill" onClick={() => send(p.q)}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-600 hover:border-[#0E9AC8] hover:text-[#0E9AC8] transition-colors">
                  <p.icon className="h-4 w-4" strokeWidth={1.75} /> {p.label}
                </button>
              ))}
            </div>
          )}

          <p className="mt-4 text-center text-xs text-slate-400">
            Free to chat, no sign-up needed.{" "}
            <button onClick={continueSignIn} data-testid="landing-chat-signin" className="font-semibold text-[#0E9AC8] hover:underline">Sign in</button>{" "}
            for a personalised Construction Health Report & cost estimates.
          </p>
        </div>
      </div>
    </section>
  );
}
